(() => {
  // Version marker: restore customizer interaction loading after theme asset refresh.
  const stateStore = window.__jerseyCustomizerState || (window.__jerseyCustomizerState = new Map());

  function mountCartFlight(root = document) {
    root.querySelectorAll('product-info .product-form__submit[name="add"]').forEach((button) => {
      if (button.dataset.jcCartFlightMounted === 'true') return;

      const host = button.parentElement;
      if (!host) return;

      button.dataset.jcCartFlightMounted = 'true';
      button.classList.add('jc-cart-flight-button');
      host.classList.add('jc-cart-flight-host');

      const track = document.createElement('span');
      const icon = document.createElement('span');
      const cart = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      track.className = 'jc-cart-flight-track';
      icon.className = 'jc-cart-flight-icon';
      cart.classList.add('jc-cart-flight-cart');
      cart.setAttribute('viewBox', '0 0 72 56');
      cart.setAttribute('focusable', 'false');
      cart.setAttribute('aria-hidden', 'true');
      cart.innerHTML = `
        <path d="M4 14h15M8 26h14M4 38h17"
          fill="none" stroke="currentColor" stroke-width="5"
          stroke-linecap="round" />
        <path d="M25 8h7.2c1.7 0 3.1 1.1 3.5 2.7l1.7 6.3h25.8c2.5 0 4.2 2.5 3.3 4.8l-6 16.1c-.5 1.3-1.8 2.1-3.2 2.1H39.1c-1.7 0-3.1-1.1-3.5-2.7L30 14h-5Z"
          fill="currentColor" />
        <circle cx="41" cy="48" r="4.3" fill="currentColor" />
        <circle cx="56" cy="48" r="4.3" fill="currentColor" />
      `;
      track.setAttribute('aria-hidden', 'true');
      icon.setAttribute('aria-hidden', 'true');
      icon.append(cart);
      host.append(track);
      button.append(icon);

      const syncTrack = () => {
        const buttonRect = button.getBoundingClientRect();
        const hostRect = host.getBoundingClientRect();
        track.style.setProperty('--jc-cart-flight-left', `${buttonRect.left - hostRect.left}px`);
        track.style.setProperty('--jc-cart-flight-top', `${buttonRect.top - hostRect.top}px`);
        track.style.setProperty('--jc-cart-flight-width', `${buttonRect.width}px`);
        track.style.setProperty('--jc-cart-flight-height', `${buttonRect.height}px`);
      };

      syncTrack();
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(syncTrack);
        observer.observe(button);
        observer.observe(host);
        track.jcCartFlightResizeObserver = observer;
      }
    });
  }

  class JerseyCustomizer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true' || this.dataset.initPending === 'true') return;

      this.dataset.initPending = 'true';
      let readinessObserver;
      const initialize = () => {
        readinessObserver?.disconnect();
        delete this.dataset.initPending;
        if (!this.isConnected || this.dataset.initialized === 'true') return;
        this.initialize();
      };

      // The customizer is rendered immediately after the Customize option, while
      // the Patch charge option can still be waiting for the HTML parser below it.
      // Initialize as soon as those native controls exist instead of waiting for
      // every product image and third-party app to finish parsing.
      const picker = this.closest('variant-picker');
      const controlsReady = () => {
        if (!picker?.querySelector('.jc-native-customize-option')) return false;
        return !this.querySelector('[data-jc-patch-card]')
          || Boolean(picker.querySelector('.jc-native-patch-option'));
      };

      if (controlsReady()) {
        queueMicrotask(initialize);
        return;
      }

      readinessObserver = new MutationObserver(() => {
        if (controlsReady()) initialize();
      });
      readinessObserver.observe(picker || document.documentElement, { childList: true, subtree: true });

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialize, { once: true });
      } else {
        requestAnimationFrame(initialize);
      }
    }

    initialize() {
      this.dataset.initialized = 'true';

      this.formId = this.dataset.formId;
      this.stateKey = `${this.dataset.productId}:${this.formId}`;
      this.patchLimit = Math.max(1, Math.min(2, Number(this.dataset.patchLimit) || 1));
      this.customPriceCents = Number(this.dataset.customPriceCents) || 500;
      this.variantPicker = this.closest('variant-picker');
      this.nativeOption = this.variantPicker?.querySelector('.jc-native-customize-option');
      this.nativePatchOption = this.variantPicker?.querySelector('.jc-native-patch-option');
      this.yesRadio = this.findNativeRadio('yes');
      this.noRadio = this.findNativeRadio('no');
      this.patchNoneRadio = this.findPatchRadio(0);
      this.patchOneRadio = this.findPatchRadio(1);
      this.patchTwoRadio = this.findPatchRadio(2);

      this.customCard = this.querySelector('[data-jc-custom-card]');
      this.customBody = this.querySelector('[data-jc-custom-body]');
      this.customSummary = this.querySelector('[data-jc-custom-summary]');
      this.customProperty = this.querySelector('[data-jc-custom-property]');
      this.customAdd = this.querySelector('[data-jc-custom-add]');
      this.nameInput = this.querySelector('[data-jc-name]');
      this.numberInput = this.querySelector('[data-jc-number]');
      this.playerSelect = this.querySelector('[data-jc-player-select]');
      this.patchInputs = Array.from(this.querySelectorAll('[data-jc-patch]'));
      this.patchProperty = this.querySelector('[data-jc-patch-property]');
      this.patchSummary = this.querySelector('[data-jc-patch-summary]');
      this.patchAdd = this.querySelector('[data-jc-patch-add]');
      this.patchRequest = null;
      try {
        this.localVariants = JSON.parse(this.querySelector('[data-jc-variant-map]')?.textContent || '[]');
      } catch (_) {
        this.localVariants = [];
      }

      this.restoreState();
      this.setupPlayerPicker();
      this.bindEvents();
      this.syncCustomizationState();
      this.evaluateCustomization({ allowVariantChange: true });
      this.updatePatchState({ allowVariantChange: true });

      const prewarm = () => this.warmPatchImages();
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(prewarm, { timeout: 1400 });
      } else {
        window.setTimeout(prewarm, 650);
      }
    }

    setupPlayerPicker() {
      if (!this.playerSelect || this.querySelector('[data-jc-player-picker]')) return;

      const nativeWrap = this.playerSelect.closest('.jc-select-wrap');
      const field = nativeWrap?.closest('.jc-field');
      if (!nativeWrap || !field) return;

      const picker = document.createElement('div');
      const trigger = document.createElement('button');
      const triggerLabel = document.createElement('span');
      const triggerIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      const menu = document.createElement('div');
      const mobileField = document.createElement('div');
      const mobileFieldLabel = document.createElement('span');
      const menuId = `JerseyPlayerPicker-${this.formId}`;

      mobileField.className = 'jc-field jc-player-picker-field';
      mobileFieldLabel.textContent = field.querySelector('span')?.textContent.trim() || 'Select player';
      picker.className = 'jc-player-picker';
      picker.dataset.jcPlayerPicker = '';
      trigger.className = 'jc-player-picker__trigger';
      trigger.type = 'button';
      trigger.dataset.jcPlayerTrigger = '';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', menuId);
      triggerLabel.dataset.jcPlayerPickerLabel = '';
      triggerIcon.setAttribute('viewBox', '0 0 20 20');
      triggerIcon.setAttribute('fill', 'none');
      triggerIcon.setAttribute('aria-hidden', 'true');
      triggerIcon.innerHTML = '<path d="m5 7.5 5 5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';
      trigger.append(triggerLabel, triggerIcon);

      menu.className = 'jc-player-picker__menu';
      menu.dataset.jcPlayerMenu = '';
      menu.id = menuId;
      menu.setAttribute('role', 'listbox');
      menu.setAttribute('aria-hidden', 'true');
      menu.setAttribute('inert', '');

      Array.from(this.playerSelect.options).forEach((nativeOption) => {
        const option = document.createElement('button');
        const optionLabel = document.createElement('span');
        const optionCheck = document.createElement('span');
        option.className = 'jc-player-picker__option';
        option.type = 'button';
        option.dataset.jcPlayerOption = '';
        option.dataset.playerValue = nativeOption.value;
        option.setAttribute('role', 'option');
        option.tabIndex = -1;
        optionLabel.textContent = nativeOption.textContent.trim();
        optionCheck.className = 'jc-player-picker__check';
        optionCheck.textContent = '✓';
        optionCheck.setAttribute('aria-hidden', 'true');
        option.append(optionLabel, optionCheck);
        option.addEventListener('click', () => {
          this.playerSelect.value = nativeOption.value;
          this.syncPlayerPicker();
          this.setPlayerPickerOpen(false);
          this.playerSelect.dispatchEvent(new Event('change', { bubbles: true }));
        });
        menu.append(option);
      });

      trigger.addEventListener('click', () => {
        this.setPlayerPickerOpen(!picker.classList.contains('is-open'));
      });
      picker.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        this.setPlayerPickerOpen(false);
        trigger.focus();
      });

      picker.append(trigger, menu);
      mobileField.append(mobileFieldLabel, picker);
      field.after(mobileField);
      field.classList.add('has-mobile-player-picker');
      this.playerPicker = picker;
      this.playerPickerTrigger = trigger;
      this.playerPickerLabel = triggerLabel;
      this.playerPickerMenu = menu;
      this.playerPickerOptions = Array.from(menu.querySelectorAll('[data-jc-player-option]'));
      this.syncPlayerPicker();
    }

    setPlayerPickerOpen(open) {
      if (!this.playerPicker || !this.playerPickerMenu || !this.playerPickerTrigger) return;
      const shouldOpen = Boolean(open && !this.playerPickerTrigger.disabled);
      this.playerPicker.classList.toggle('is-open', shouldOpen);
      this.playerPickerMenu.setAttribute('aria-hidden', String(!shouldOpen));
      this.playerPickerMenu.toggleAttribute('inert', !shouldOpen);
      this.playerPickerTrigger.setAttribute('aria-expanded', String(shouldOpen));
      this.playerPickerOptions?.forEach((option) => {
        option.tabIndex = shouldOpen ? 0 : -1;
      });
    }

    syncPlayerPicker() {
      if (!this.playerSelect || !this.playerPickerTrigger || !this.playerPickerLabel) return;
      const selected = this.playerSelect.selectedOptions[0];
      const value = selected?.value || '';
      this.playerPickerLabel.textContent = selected?.textContent.trim() || 'Select a player';
      this.playerPickerTrigger.disabled = this.playerSelect.disabled;
      this.playerPickerOptions?.forEach((option) => {
        option.setAttribute('aria-selected', String(option.dataset.playerValue === value));
      });
    }

    findNativeRadio(kind) {
      return Array.from(this.nativeOption?.querySelectorAll('input[type="radio"]') || []).find((radio) => {
        const value = String(radio.value || '').trim().toLowerCase();
        return kind === 'yes' ? /^yes\b/.test(value) : /^no\b/.test(value);
      });
    }

    findPatchRadio(count) {
      return Array.from(this.nativePatchOption?.querySelectorAll('input[type="radio"]') || []).find((radio) => {
        const value = String(radio.value || '').trim().toLowerCase();
        if (count === 0) return /^(none|no\s*patch|0\s*patch)/.test(value);
        if (count === 1) return /^(1|one|single)\b/.test(value);
        return /^(2|two|double)\b/.test(value);
      });
    }

    bindEvents() {
      this.querySelector('[data-jc-custom-toggle]')?.addEventListener('click', () => {
        this.setCustomOpen(!this.customCard.classList.contains('is-open'));
      });

      this.querySelector('[data-jc-clear]')?.addEventListener('click', () => this.clearCustomization());

      this.querySelectorAll('[data-jc-tab]').forEach((tab) => {
        tab.addEventListener('click', () => this.selectTab(tab.dataset.jcTab));
      });

      this.nameInput?.addEventListener('input', () => {
        this.nameInput.value = this.nameInput.value.toUpperCase().slice(0, 15);
        if (this.playerSelect) {
          this.playerSelect.value = '';
          this.syncPlayerPicker();
        }
        this.evaluateCustomization({ allowVariantChange: true });
      });

      this.numberInput?.addEventListener('input', () => {
        this.numberInput.value = this.numberInput.value.replace(/\D/g, '').slice(0, 2);
        if (this.playerSelect) {
          this.playerSelect.value = '';
          this.syncPlayerPicker();
        }
        this.evaluateCustomization({ allowVariantChange: true });
      });

      this.playerSelect?.addEventListener('change', () => {
        this.syncPlayerPicker();
        const option = this.playerSelect.selectedOptions[0];
        if (!option?.value) {
          if (this.nameInput) this.nameInput.value = '';
          if (this.numberInput) this.numberInput.value = '';
          this.evaluateCustomization({ allowVariantChange: true });
          return;
        }
        if (this.nameInput) this.nameInput.value = String(option.dataset.playerName || '').toUpperCase().slice(0, 15);
        if (this.numberInput) this.numberInput.value = String(option.dataset.playerNumber || '').replace(/\D/g, '').slice(0, 2);
        this.evaluateCustomization({ allowVariantChange: true });
      });

      this.querySelector('[data-jc-patch-toggle]')?.addEventListener('click', () => {
        const card = this.querySelector('[data-jc-patch-card]');
        if (!card) return;
        const willOpen = !card.classList.contains('is-open');
        if (willOpen) this.warmPatchImages();
        card.classList.toggle('is-open', willOpen);
        this.querySelector('[data-jc-patch-toggle]').setAttribute('aria-expanded', String(willOpen));
      });

      this.patchInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked && this.patchLimit === 1) {
            this.patchInputs.forEach((other) => {
              if (other !== input) other.checked = false;
            });
          }

          const selected = this.patchInputs.filter((item) => item.checked);
          if (selected.length > this.patchLimit) input.checked = false;
          this.saveState();
          this.updatePatchState({ allowVariantChange: true });
        });
      });

    }

    evaluateCustomization({ allowVariantChange = false } = {}) {
      const name = this.nameInput?.value.trim() || '';
      const number = this.numberInput?.value.trim() || '';
      const hasName = name.length > 0;
      const hasNumber = /^[0-9]{1,2}$/.test(number);
      const complete = hasName && hasNumber;

      if (this.nameInput) {
        this.nameInput.setCustomValidity(!hasName && number ? 'Please enter the name for this jersey.' : '');
      }
      if (this.numberInput) {
        this.numberInput.setCustomValidity(hasName && !hasNumber ? 'Please enter a 1 or 2 digit jersey number.' : '');
      }

      this.setPropertySubmission(complete);
      this.customCard?.classList.toggle('is-active', complete);
      this.updateCustomizationPrice(complete);
      this.updateCustomizationSummary();
      this.saveState({ active: complete });

      if (!allowVariantChange) return;
      if (complete && this.yesRadio && !this.yesRadio.checked) {
        this.saveState({ active: true, open: true });
        this.yesRadio.checked = true;
        this.refreshPatchVariant(this.yesRadio);
      } else if (!complete && this.noRadio && !this.noRadio.checked) {
        this.saveState({ active: false });
        this.noRadio.checked = true;
        this.refreshPatchVariant(this.noRadio);
      }
    }

    clearCustomization() {
      if (this.nameInput) this.nameInput.value = '';
      if (this.numberInput) this.numberInput.value = '';
      if (this.playerSelect) this.playerSelect.value = '';
      this.syncPlayerPicker();
      this.setPlayerPickerOpen(false);
      this.setPropertySubmission(false);
      this.customCard?.classList.remove('is-active');
      this.updateCustomizationPrice(false);
      this.setCustomOpen(false);
      this.updateCustomizationSummary();
      this.saveState({ active: false, open: false, name: '', number: '', playerValue: '' });
      if (this.noRadio && !this.noRadio.checked) {
        this.noRadio.checked = true;
        this.refreshPatchVariant(this.noRadio);
      }
    }

    syncCustomizationState() {
      const selectedYes = Boolean(this.yesRadio?.checked || /^yes\b/i.test(this.dataset.customizeSelected || ''));
      const state = stateStore.get(this.stateKey);
      if (selectedYes) {
        this.customCard?.classList.add('is-active');
        if (!state) this.setCustomOpen(true, false);
      }
    }

    setPropertySubmission(enabled) {
      [this.customProperty, this.nameInput, this.numberInput].forEach((input) => {
        if (!input) return;
        const propertyName = input.dataset.propertyName;
        if (enabled && propertyName) input.setAttribute('name', propertyName);
        else input.removeAttribute('name');
      });
      [this.nameInput, this.numberInput].forEach((input) => {
        if (input) input.required = enabled;
      });
    }

    setCustomOpen(open, persist = true) {
      if (!this.customCard) return;
      this.customCard.classList.toggle('is-open', open);
      this.querySelector('[data-jc-custom-toggle]')?.setAttribute('aria-expanded', String(open));
      if (!open) this.setPlayerPickerOpen(false);
      if (persist) this.saveState({ open });
    }

    selectTab(name) {
      const clearSelectedPlayer = name === 'manual' && Boolean(this.playerSelect?.value);

      this.querySelectorAll('[data-jc-tab]').forEach((tab) => {
        const selected = tab.dataset.jcTab === name;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
      });
      this.querySelectorAll('[data-jc-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.jcPanel !== name;
      });
      if (name !== 'player') this.setPlayerPickerOpen(false);

      if (clearSelectedPlayer) {
        if (this.nameInput) this.nameInput.value = '';
        if (this.numberInput) this.numberInput.value = '';
        this.playerSelect.value = '';
        this.syncPlayerPicker();
        this.saveState({ tab: name, active: false, name: '', number: '', playerValue: '' });
        this.evaluateCustomization({ allowVariantChange: true });
        return;
      }

      this.saveState({ tab: name });
    }

    updateCustomizationSummary() {
      if (!this.customSummary) return;
      const name = this.nameInput?.value.trim();
      const number = this.numberInput?.value.trim();
      const complete = Boolean(name && /^[0-9]{1,2}$/.test(number || ''));
      if (!complete) {
        this.customSummary.hidden = true;
        this.customSummary.textContent = '';
        return;
      }
      this.customSummary.textContent = `${name} · #${number}`;
      this.customSummary.hidden = false;
    }

    updateCustomizationPrice(active) {
      if (!this.customAdd) return;
      this.customAdd.textContent = active ? `+ ${this.formatMoney(this.customPriceCents)}` : '+ Add';
      this.customAdd.classList.toggle('is-priced', active);
    }

    warmPatchImages() {
      this.patchInputs.slice(0, 4).forEach((input) => {
        const image = input.closest('[data-jc-patch-row]')?.querySelector('img');
        if (!image) return;
        image.loading = 'eager';
        image.decoding = 'async';
        image.fetchPriority = 'low';
        image.decode?.().catch(() => {});
      });
    }

    getSelectedOptionControls() {
      if (!this.variantPicker) return [];
      return Array.from(this.variantPicker.querySelectorAll(
        'fieldset input:checked[data-option-value-id], select option:checked[data-option-value-id]'
      ));
    }

    findLocalVariant() {
      if (!Array.isArray(this.localVariants) || !this.localVariants.length) return null;
      const selectedValues = this.getSelectedOptionControls().map((option) => String(option.value));
      if (!selectedValues.length) return null;
      return this.localVariants.find((variant) => (
        Array.isArray(variant.options)
        && variant.options.length === selectedValues.length
        && variant.options.every((value, index) => String(value) === selectedValues[index])
      )) || null;
    }

    applyVariant(variant) {
      if (!variant?.id) return false;
      const productInfo = this.closest('product-info');
      productInfo?.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
        input.value = String(variant.id);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      const currentVariantData = productInfo?.querySelector('[data-selected-variant]');
      if (currentVariantData) currentVariantData.textContent = JSON.stringify(variant);

      const pageUrl = new URL(window.location.href);
      pageUrl.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', pageUrl);
      return true;
    }

    applyLocalVariant() {
      return this.applyVariant(this.findLocalVariant());
    }

    updatePatchState({ allowVariantChange = false } = {}) {
      const selected = this.patchInputs.filter((input) => input.checked);
      const atLimit = selected.length >= this.patchLimit;

      this.patchInputs.forEach((input) => {
        const shouldDisable = this.patchLimit > 1 && atLimit && !input.checked;
        input.disabled = shouldDisable;
        input.closest('[data-jc-patch-row]')?.classList.toggle('is-limit-disabled', shouldDisable);
      });

      const names = selected.map((input) => input.dataset.patchName).filter(Boolean);
      if (this.patchProperty) {
        this.patchProperty.value = names.join(' + ');
        this.patchProperty.disabled = names.length === 0;
      }

      if (this.patchSummary) {
        const chip = document.createElement('span');
        chip.className = names.length > 0 ? 'jc-chip' : 'jc-chip jc-chip--empty';
        chip.textContent = names.length > 0 ? names.join(' · ') : 'No patches selected';
        this.patchSummary.replaceChildren(chip);
        this.patchSummary.hidden = false;
      }

      if (this.patchAdd) {
        const totalCents = selected.reduce((total, input) => total + (Number(input.dataset.patchPriceCents) || 0), 0);
        this.patchAdd.textContent = totalCents > 0 ? `+ ${this.formatMoney(totalCents)}` : '+ Add';
        this.patchAdd.classList.toggle('is-priced', totalCents > 0);
      }

      this.querySelector('[data-jc-patch-card]')?.classList.toggle('is-active', selected.length > 0);

      if (!allowVariantChange) return;
      const targetRadio = selected.length === 0
        ? this.patchNoneRadio
        : selected.length === 1
          ? this.patchOneRadio
          : this.patchTwoRadio;
      if (targetRadio && !targetRadio.checked) {
        this.saveState();
        targetRadio.checked = true;
        this.refreshPatchVariant(targetRadio);
      }
    }

    async refreshPatchVariant(targetRadio) {
      const productInfo = this.closest('product-info');
      const sectionId = productInfo?.dataset.sectionId;
      const productId = productInfo?.dataset.productId || this.dataset.productId;
      const productUrl = productInfo?.dataset.productUrl || window.location.pathname;
      if (!sectionId || !productId || !this.variantPicker) {
        targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const selectedOptionValues = Array.from(
        this.getSelectedOptionControls()
      ).map((option) => option.getAttribute('data-option-value-id')).filter(Boolean);

      // The correct sellable variant is already embedded in the page. Apply its
      // ID immediately so Add to cart never waits for the visual section refresh.
      this.applyLocalVariant();

      if (!selectedOptionValues.length) {
        targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      this.patchRequest?.abort();
      this.patchRequest = new AbortController();

      const requestUrl = new URL(productUrl, window.location.origin);
      requestUrl.searchParams.set('section_id', sectionId);
      requestUrl.searchParams.set('option_values', selectedOptionValues.join(','));

      try {
        const response = await fetch(requestUrl, { signal: this.patchRequest.signal });
        if (!response.ok) throw new Error(`Patch variant request failed: ${response.status}`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const variantData = html.querySelector('[data-selected-variant]')?.textContent;
        if (!variantData) throw new Error('No matching patch variant found');

        const variant = JSON.parse(variantData);
        ['Price', 'BuyButtonPrice', 'StickyPrice'].forEach((prefix) => {
          const elementId = `${prefix}-${sectionId}-${productId}`;
          const source = html.getElementById(elementId);
          const destination = document.getElementById(elementId);
          if (source && destination) destination.innerHTML = source.innerHTML;
        });

        this.applyVariant(variant);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[Jersey customizer]', error);
          targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency
        }).format(cents / 100);
      } catch (_) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    saveState(overrides = {}) {
      const current = stateStore.get(this.stateKey) || {};
      stateStore.set(this.stateKey, {
        ...current,
        active: Boolean(this.customCard?.classList.contains('is-active')),
        open: Boolean(this.customCard?.classList.contains('is-open')),
        name: this.nameInput?.value || '',
        number: this.numberInput?.value || '',
        tab: this.querySelector('[data-jc-tab].is-active')?.dataset.jcTab || 'manual',
        playerValue: this.playerSelect?.value || '',
        patches: this.patchInputs.filter((input) => input.checked).map((input) => input.value),
        ...overrides
      });
    }

    restoreState() {
      const state = stateStore.get(this.stateKey);
      if (!state) return;
      if (this.nameInput) this.nameInput.value = state.name || '';
      if (this.numberInput) this.numberInput.value = state.number || '';
      if (this.playerSelect && state.playerValue) this.playerSelect.value = state.playerValue;

      if (state.tab) {
        this.querySelectorAll('[data-jc-tab]').forEach((tab) => {
          const selected = tab.dataset.jcTab === state.tab;
          tab.classList.toggle('is-active', selected);
          tab.setAttribute('aria-selected', String(selected));
        });
        this.querySelectorAll('[data-jc-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.jcPanel !== state.tab;
        });
      }

      this.patchInputs.forEach((input) => {
        input.checked = Array.isArray(state.patches) && state.patches.includes(input.value);
      });
      this.setCustomOpen(Boolean(state.open), false);
    }
  }

  if (!customElements.get('jersey-customizer')) {
    customElements.define('jersey-customizer', JerseyCustomizer);
  }

  const startCartFlight = () => mountCartFlight(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startCartFlight, { once: true });
  } else {
    startCartFlight();
  }
  document.addEventListener('shopify:section:load', (event) => mountCartFlight(event.target));
})();
